import React, { useEffect, useState, useCallback, useReducer } from "react";
import { PageLevelAlert, Chip } from "@kuda/ui";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading";
import Dialogbox from "../components/dialogs/dialogBox";
import useAccessToken from "../auth/accessPermission";
import Breadcrumbs from "../components/breadcrumbs";
import useUserAccount from "../components/hooks/userAccountInfo";
import FormFields from "../components/secretForm/FormFields";
import SecretEditor from "../components/secretForm/SecretEditor";
import AwsProfileSelector from "../components/AwsProfileSelector";
import { useSecrets } from "../components/hooks/useSecrets";
import { createTags, validateJson } from "../components/utils/tagHelpers";
import { config, getClusterProviders } from "../components/config";

const initialState = {
  secretName: "",
  jsonInput: "",
  envName: "",
  projName: "",
  secretType: "",
  selectedClusters: [],
  clusterMeta: [],
  selectedFile: null,
  fileContent: "",
  openCreate: false,
  openUpload: false,
  inputError: "",
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_MULTIPLE':
      return { ...state, ...action.values };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
}

function CreateSecretForm() {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openBCWarning, setOpenBCWarning] = useState(false);
  const [openError, setOpenError] = useState(false);

  // IMPORTANT: declare clusterProviders BEFORE any useEffect that references it
  const [clusterProviders, setClusterProviders] = useState([]);
  const [isAwsPlatform, setIsAwsPlatform] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { nameParts, roles } = useUserAccount(instance);
  const accessToken = useAccessToken([config.apiAccess]);

  const userRole = roles.length > 0 ? roles[0] : '';
  const { loading, createSecret } = useSecrets();

  const searchParams = new URLSearchParams(location.search);
  const urlAwsProfile = searchParams.get('awsProfile');

  const [awsProfile, setAwsProfile] = useState(() => {
    if (urlAwsProfile && config.awsProfiles[urlAwsProfile]) return urlAwsProfile;
    return 'profile-a';
  });

  // Auto-fill project name for non-admin roles
  useEffect(() => {
    const projectRole = roles.find(r =>
      !config.rbac.adminRoles.includes(r) && !config.rbac.elevatedRoles.includes(r)
    );
    if (projectRole) {
      dispatch({ type: 'SET_FIELD', field: 'projName', value: projectRole });
    }
  }, [userRole]);

  useEffect(() => {
    if (state.fileContent) {
      dispatch({ type: 'SET_FIELD', field: 'jsonInput', value: state.fileContent });
    }
  }, [state.fileContent]);

  // Update isAwsPlatform whenever clusterProviders changes
  useEffect(() => {
    const hasAws = clusterProviders.some(
      p => p.active && p.platformName === config.platforms[0]
    );
    setIsAwsPlatform(hasAws);
  }, [clusterProviders]);

  // Cluster change — expand each cluster into one entry per provider
  const handleClusterChange = useCallback((clusters) => {
    const expanded = clusters.flatMap((c) =>
      getClusterProviders(c).map(({ platformName, vaultId }) => ({
        cluster: c,
        platformName,
        vaultId,
        active: true,
      }))
    );
    setClusterProviders(expanded);
    const meta = expanded.map(({ cluster, platformName, vaultId }) => ({
      cluster, platformName, vaultId,
    }));
    dispatch({ type: 'SET_MULTIPLE', values: { selectedClusters: clusters, clusterMeta: meta } });
  }, []);

  // Toggle a provider chip on/off
  const handleToggleProvider = useCallback((cluster, platformName) => {
    setClusterProviders((prev) => {
      const updated = prev.map((p) =>
        p.cluster === cluster && p.platformName === platformName
          ? { ...p, active: !p.active }
          : p
      );
      const meta = updated
        .filter((p) => p.active)
        .map(({ cluster, platformName, vaultId }) => ({ cluster, platformName, vaultId }));
      dispatch({ type: 'SET_FIELD', field: 'clusterMeta', value: meta });
      return updated;
    });
  }, []);

  const handleSecretNameChange = useCallback((e) => {
    const value = e.target.value;
    const regex = /^[A-Za-z][A-Za-z0-9_-]*$/;
    if (regex.test(value) || value === "") {
      dispatch({ type: 'SET_MULTIPLE', values: { secretName: value, inputError: "" } });
    } else {
      dispatch({ type: 'SET_FIELD', field: 'inputError', value: "Must start with a letter, you can also add [0-9, -,_]" });
    }
  }, []);

  const handleOpenWarning = () => {
    setOpenBCWarning(true);
    setWarningMessage("Changes not saved!");
  };

  const handleAwsProfileChange = useCallback((profile) => {
    setAwsProfile(profile);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("awsProfile", profile);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  }, [location, navigate]);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        dispatch({ type: 'SET_FIELD', field: 'fileContent', value: e.target.result });
      };
      reader.readAsText(file);
      dispatch({ type: 'SET_MULTIPLE', values: { selectedFile: file, openUpload: false } });
    }
  }, []);

  const handleCreateSecret = useCallback(async () => {
    const { secretName, jsonInput, envName, projName, secretType, clusterMeta } = state;

    if (!jsonInput || !secretName || !envName || !projName || !secretType) {
      setWarningMessage("All empty fields must be supplied");
      setOpenWarning(true);
      return;
    }

    if (!validateJson(jsonInput)) {
      setErrorMessage("Syntax error please check your input");
      setOpenError(true);
      return;
    }

    if (!clusterMeta.length) {
      setWarningMessage("Select at least one cluster");
      setOpenWarning(true);
      return;
    }

    const newJsonInput = secretType === "appsettings_file"
      ? `{"appsettings.json": ${jsonInput}}`
      : jsonInput;

    const results = [];
    for (const { cluster, platformName, vaultId } of clusterMeta) {
      const tags = createTags(nameParts, envName, projName, platformName);
      // Some Vault engines store secrets under nested <environment>/<team>/<name>
      // paths rather than a flat namespace — derive the write path from the
      // same Environment/Team fields already required above for tagging.
      const usesNestedVaultPath = platformName === "vault" && config.vaultNestedPathEngines.includes(vaultId);
      const finalSecretName = usesNestedVaultPath ? `${envName}/${projName}/${secretName}` : secretName;
      const secretData = { secretName: finalSecretName, secretValue: newJsonInput, tags };

      const rolesHeader = roles?.join(",") ?? "";
      let headersForCluster = {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        Roles: rolesHeader,
        "X-User-Display-Name": nameParts?.join(" ") || "",
      };

      if (platformName === config.platforms[0]) {
        const profileConfig = config.awsProfiles[awsProfile];
        const profileRegion = profileConfig?.region || vaultId;
        headersForCluster.smRegion = profileRegion;
        if (profileConfig) headersForCluster.smAccount = profileConfig.account;
      } else {
        headersForCluster.vaultName = vaultId;
      }

      try {
        const resp = await createSecret(secretData, platformName, headersForCluster);
        results.push({ cluster, platformName, vaultId, ok: resp.status === 200 });
      } catch (err) {
        const backendError = err.response?.data?.error || "Unknown error";
        results.push({ cluster, platformName, vaultId, ok: false, error: backendError });
      }
    }

    const failures = results.filter((r) => !r.ok);
    if (!failures.length) {
      setOpenSuccess(true);
    } else {
      setErrorMessage(
        failures.map((f) => `${f.cluster} (${f.platformName}): ${f.error || "failed"}`).join("; ")
      );
      setOpenError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, nameParts, accessToken, JSON.stringify(roles), createSecret, awsProfile]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);
    // Small delay to allow AWS/Vault eventual consistency before list refetches
    setTimeout(() => {
      if (state.clusterMeta && state.clusterMeta.length === 1) {
        const { platformName, cluster, vaultId } = state.clusterMeta[0];
        const params = new URLSearchParams({ platformName, selectedCluster: cluster, vaultName: vaultId });
        if (platformName === config.platforms[0]) {
          params.set('awsProfile', awsProfile);
          const profileRegion = config.awsProfiles[awsProfile]?.region;
          if (profileRegion) params.set('vaultName', profileRegion);
        }
        const usesNestedVaultPath = platformName === "vault" && config.vaultNestedPathEngines.includes(vaultId);
        const finalSecretName = usesNestedVaultPath
          ? `${state.envName}/${state.projName}/${state.secretName}`
          : state.secretName;
        navigate(`/view-secret/${finalSecretName}?${params.toString()}`);
      } else {
        const params = new URLSearchParams({ selectedCluster: state.selectedClusters?.[0] || '' });
        if (isAwsPlatform) params.set('awsProfile', awsProfile);
        navigate(`/secrets?${params.toString()}`);
      }
    }, 1500); // 1.5s gives AWS/Vault time to propagate
  }, [state, navigate, awsProfile, isAwsPlatform]);

  const activeProviders = clusterProviders.filter(p => p.active);
  const multiProvider   = clusterProviders.length > 1;

  return (
    <div>
      <div className="pl-8 mb-4">
        <Breadcrumbs
          onIsModified={handleOpenWarning}
          isChange={state.selectedFile || state.openCreate}
        />
      </div>

      <div className="flex flex-col items-center" style={{ minHeight: "100vh" }}>
        {loading && <Loading open={loading} />}

        <div className="flex justify-between items-center mb-4" style={{ width: '60rem' }}>
          <h1 className="font-heading text-3xl font-bold text-color-text-brand-primary-default" style={{ margin: 30 }}>
            New secret
          </h1>
          {isAwsPlatform && (
            <AwsProfileSelector
              selectedProfile={awsProfile}
              onProfileChange={handleAwsProfileChange}
              platformName={config.platforms[0]}
            />
          )}
        </div>

        {isAwsPlatform && config.awsProfiles[awsProfile] && (
          <div className="mb-4" style={{ width: '60rem' }}>
            <PageLevelAlert type="info" width="100%">
              <strong>AWS Profile:</strong> {config.awsProfiles[awsProfile].name} |{' '}
              Region: {config.awsProfiles[awsProfile].region} |{' '}
              Account: {config.awsProfiles[awsProfile].account}
              <br />
              <span className="text-xs">Secret will be created in this AWS account and region</span>
            </PageLevelAlert>
          </div>
        )}

        <div className="flex flex-col items-center">
          <FormFields
            secretName={state.secretName}
            onSecretNameChange={handleSecretNameChange}
            inputError={state.inputError}
            secretType={state.secretType}
            onSecretTypeChange={(value) => dispatch({ type: 'SET_FIELD', field: 'secretType', value })}
            envName={state.envName}
            onEnvChange={(value) => dispatch({ type: 'SET_FIELD', field: 'envName', value })}
            projName={state.projName}
            onProjChange={(value) => dispatch({ type: 'SET_FIELD', field: 'projName', value })}
            userRole={userRole}
            onClusterUserRole={userRole}
            onClusterChange={handleClusterChange}
          />

          {/* Provider chips — shown when a cluster is selected */}
          {clusterProviders.length > 0 && (
            <div className="mb-4" style={{ width: '60rem' }}>
              <span className="block mb-1 text-xs font-bold text-color-text-secondary uppercase" style={{ letterSpacing: '.05em' }}>
                Target Providers {multiProvider ? '— click to toggle' : ''}
              </span>
              <div className="flex flex-row flex-wrap gap-2">
                {clusterProviders.map((p) => (
                  <Chip
                    key={`${p.cluster}-${p.platformName}`}
                    label={`${p.cluster} · ${p.platformName.toUpperCase()}`}
                    type="selectable"
                    selected={p.active}
                    onClick={multiProvider ? () => handleToggleProvider(p.cluster, p.platformName) : undefined}
                  />
                ))}
              </div>
              {activeProviders.length > 1 && (
                <span className="block mt-1 text-xs text-color-text-secondary">
                  Secret will be written to {activeProviders.map(p => p.platformName.toUpperCase()).join(' and ')} simultaneously
                </span>
              )}
              {clusterProviders.some(p => !p.active) && (
                <span className="block mt-1 text-xs text-color-text-state-warning-default">
                  Greyed out providers will be skipped
                </span>
              )}
            </div>
          )}

          <SecretEditor
            openCreate={state.openCreate}
            openUpload={state.openUpload}
            selectedFile={state.selectedFile}
            jsonInput={state.jsonInput}
            fileContent={state.fileContent}
            onCreateClick={() => dispatch({ type: 'SET_FIELD', field: 'openCreate', value: true })}
            onUploadClick={() => dispatch({ type: 'SET_FIELD', field: 'openUpload', value: true })}
            onFileChange={handleFileChange}
            onCodeChange={(value) => dispatch({ type: 'SET_FIELD', field: 'jsonInput', value })}
            onCreateSecret={handleCreateSecret}
            onCancel={() => {
              setOpenWarning(true);
              setWarningMessage("Changes not saved!");
            }}
          />

          <div className="w-full">
            <Dialogbox
              open={openSuccess}
              onClose={handleSuccessAlertClose}
              title="Success"
              severity="success"
              message={
                <div>
                  {state.secretName} created successfully! <br />
                  Click on the close button to view secret
                </div>
              }
            />
            <Dialogbox
              open={openWarning}
              onClose={() => setOpenWarning(false)}
              title="Warning"
              severity="warning"
              message={
                <div>
                  {warningMessage} <br />
                  Click on the button to continue editing or discard
                </div>
              }
              onConfirm={() => navigate(0)}
              confirmText="Discard"
              closeText="Back to editing"
            />
            <Dialogbox
              open={openBCWarning}
              onClose={() => setOpenBCWarning(false)}
              title="Warning"
              severity="warning"
              message={
                <div>
                  {warningMessage} <br />
                  Click on the button to continue editing or discard
                </div>
              }
              onConfirm={() => navigate("/")}
              confirmText="Discard"
              closeText="Back to editing"
            />
            <Dialogbox
              open={openError}
              onClose={() => setOpenError(false)}
              title="Error"
              severity="error"
              message={
                <div>
                  Failed to create! <br />
                  Message: {errorMessage}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateSecretForm;
